import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, AlertCircle, Clock, FileText, ShieldAlert,
  Pause, Play, MessageSquare, RefreshCw, X
} from 'lucide-react';
import { ContractsV2Shell } from './DemoShell';
import {
  useContractsV2, selectFundCoverage, isMilestoneAssetCovered, computeAcceptancePeriodDays,
} from './ContractsV2Context';
import type {
  ContractV2Bundle, Milestone, PaymentPlanEntry, ReleasePlanEntry,
  PaymentEntryStatus, MilestoneStatus,
} from './mockTypes';

const formatRmb = (n: number) => `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const formatDate = (ms?: number) => ms ? new Date(ms).toLocaleDateString('zh-CN') : '—';
const formatDateTime = (ms?: number) => ms ? new Date(ms).toLocaleString('zh-CN') : '—';

// =====================================================================
// Page
// =====================================================================

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { bundleById, isViewerClient, isViewerCreator } = useContractsV2();
  const bundle = id ? bundleById(id) : undefined;

  if (!bundle) {
    return (
      <ContractsV2Shell>
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">合同不存在</p>
          <button onClick={() => nav('/contracts-v2')} className="mt-4 text-sm text-blue-600">
            返回列表
          </button>
        </div>
      </ContractsV2Shell>
    );
  }

  const cov = selectFundCoverage(bundle);
  const isClient = isViewerClient(bundle);
  const isCreator = isViewerCreator(bundle);

  return (
    <ContractsV2Shell>
      <Link to="/contracts-v2" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> 返回列表
      </Link>

      {/* Header card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-6 mb-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                bundle.contract.projectType === 'large' ? 'bg-purple-50 text-purple-700' : 'bg-sky-50 text-sky-700'
              }`}>
                {bundle.contract.projectType === 'large' ? '大项目' : '小项目'}
              </span>
              <StateBadge state={bundle.contract.acceptanceState} />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">{bundle.contract.title}</h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{bundle.contract.description}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-semibold text-slate-900">{formatRmb(bundle.contract.totalAmount)}</div>
            <div className="text-xs text-slate-400 mt-0.5">总金额</div>
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm border-t border-slate-100 pt-4">
          <PartyChip role="客户" nickname={bundle.contract.client.nickname} highlighted={isClient} />
          <PartyChip role="创作者" nickname={bundle.contract.creator.nickname} highlighted={isCreator} />
          <div className="ml-auto text-xs text-slate-400">
            创建于 {formatDate(bundle.contract.createdAt)}
            {bundle.contract.signedAt && <> · 签署于 {formatDate(bundle.contract.signedAt)}</>}
          </div>
        </div>

        {/* Fund coverage banner — only relevant for 大项目 */}
        {bundle.contract.projectType === 'large' && bundle.contract.acceptanceState === 'active' && (
          <div className="mt-5 grid grid-cols-6 gap-3 pt-4 border-t border-slate-100">
            <Metric label="项目总金额" value={formatRmb(bundle.contract.totalAmount)} />
            <Metric label="已付款" value={formatRmb(cov.totalPaid)} />
            <Metric label="项目资金余额" value={formatRmb(cov.availableBalance)} positive={cov.availableBalance > 0} />
            <Metric label="已放款" value={formatRmb(cov.totalReleased)} />
            <Metric label="待放款" value={formatRmb(bundle.contract.totalAmount - cov.totalReleased)} />
            <Metric label="尾款" value={formatRmb(bundle.contract.tailAmount)} hint={`${bundle.contract.tailPercent}%`} />
          </div>
        )}
      </div>

      {/* 大项目 timeline + payment/release plans */}
      {bundle.contract.projectType === 'large' ? (
        <LargeProjectView bundle={bundle} />
      ) : (
        <SmallProjectView bundle={bundle} />
      )}
    </ContractsV2Shell>
  );
};

// =====================================================================
// Large project main view — milestone timeline + plans sidebar
// =====================================================================

const LargeProjectView: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 space-y-3">
        <h2 className="text-sm font-medium text-slate-700">里程碑</h2>
        {bundle.milestones.map(m => (
          <MilestoneCard key={m.id} bundle={bundle} milestone={m} />
        ))}
        <ChangesSection bundle={bundle} />
        <DisputesSection bundle={bundle} />
      </div>
      <div className="col-span-1 space-y-6">
        <PaymentPlanPanel bundle={bundle} />
        <ReleasePlanPanel bundle={bundle} />
      </div>
    </div>
  );
};

// =====================================================================
// Small project view — single delivery, simpler layout
// =====================================================================

const SmallProjectView: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2">
        {bundle.milestones.map(m => (
          <MilestoneCard key={m.id} bundle={bundle} milestone={m} />
        ))}
        <ChangesSection bundle={bundle} />
        <DisputesSection bundle={bundle} />
      </div>
      <div className="col-span-1 space-y-6">
        <PaymentPlanPanel bundle={bundle} />
        <ReleasePlanPanel bundle={bundle} />
      </div>
    </div>
  );
};

// =====================================================================
// Milestone card with creator/client actions
// =====================================================================

const MilestoneCard: React.FC<{ bundle: ContractV2Bundle; milestone: Milestone }> = ({ bundle, milestone }) => {
  const { isViewerClient, isViewerCreator, dispatch } = useContractsV2();
  const isClient = isViewerClient(bundle);
  const isCreator = isViewerCreator(bundle);
  const covered = isMilestoneAssetCovered(bundle, milestone.id);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <MilestoneStatusPill status={milestone.status} />
            <CoverageBadge covered={covered} />
            <span className="text-xs text-slate-400">截止 {formatDate(milestone.dueAt)}</span>
            {milestone.acceptancePeriodEndsAt && milestone.status === 'IN_ACCEPTANCE' && (
              <span className="text-xs text-amber-600 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> 验收期至 {formatDate(milestone.acceptancePeriodEndsAt)}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900">{milestone.title}</h3>
          <p className="text-sm text-slate-500 mt-1">{milestone.description}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold text-slate-900">{formatRmb(milestone.amount)}</div>
          <div className="text-[11px] text-slate-400">本阶段金额</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100 text-xs">
        <div>
          <div className="text-slate-400 mb-1">验收标准</div>
          <div className="text-slate-700">{milestone.acceptanceCriteria}</div>
        </div>
        <div>
          <div className="text-slate-400 mb-1">交付物</div>
          <div className="text-slate-700">
            {milestone.requiredAssets.map((a, i) => (
              <span key={i} className="inline-block mr-1.5 mb-1 text-[11px] px-1.5 py-0.5 bg-slate-100 rounded">
                {a.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {milestone.submittedAssets.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-400 mb-1.5">已提交</div>
          <ul className="text-xs space-y-1">
            {milestone.submittedAssets.map((a, i) => (
              <li key={i} className="text-slate-700">
                ✓ {a.label} <span className="text-slate-400 ml-1">{formatDateTime(a.uploadedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <RevisionHistory milestone={milestone} />

      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center gap-3">
        <span>已用修改次数 {milestone.revisionCountUsed}/{milestone.revisionLimit}</span>
        {(milestone.revisionHistory || []).length === 0 && milestone.revisionDueAt && (
          <span>· 返修截止 {formatDate(milestone.revisionDueAt)}</span>
        )}
      </div>

      {/* Action row */}
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
        {/* Creator actions */}
        {isCreator && (
          <>
            {milestone.status === 'IN_PROGRESS' || milestone.status === 'REVISION_REQUESTED' || milestone.status === 'SUBMITTED' ? (
              covered ? (
                <button
                  onClick={() => setSubmitOpen(true)}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 inline-flex items-center gap-1.5"
                >
                  <Play className="w-3 h-3" /> 提交交付
                </button>
              ) : (
                <button
                  disabled
                  title="当前阶段未资金覆盖，可暂停工作"
                  className="px-3 py-1.5 bg-slate-100 text-slate-400 rounded-md text-xs font-medium inline-flex items-center gap-1.5"
                >
                  <Pause className="w-3 h-3" /> 暂停工作（未资金覆盖）
                </button>
              )
            ) : null}
            {milestone.status === 'NOT_STARTED' && covered && (
              <button
                onClick={() => setSubmitOpen(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium inline-flex items-center gap-1.5"
              >
                <Play className="w-3 h-3" /> 开始并提交
              </button>
            )}
          </>
        )}

        {/* Client actions */}
        {isClient && milestone.status === 'IN_ACCEPTANCE' && (
          <>
            <button
              onClick={() => dispatch({ type: 'CLIENT_ACCEPT_MILESTONE', contractId: bundle.contract.id, milestoneId: milestone.id })}
              className="px-3 py-1.5 bg-slate-900 text-white rounded-md text-xs font-medium hover:bg-slate-800 inline-flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3 h-3" /> 确认验收
            </button>
            <button
              onClick={() => setRevisionOpen(true)}
              className="px-3 py-1.5 border border-slate-200 text-slate-700 rounded-md text-xs font-medium hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" /> 要求修改 / 变更
            </button>
            <button
              onClick={() => dispatch({ type: 'AUTO_ACCEPT_TIMER_FIRED', contractId: bundle.contract.id, milestoneId: milestone.id })}
              title="演示用：模拟客户超时未操作触发的自动验收"
              className="px-2 py-1 text-[10px] text-slate-400 border border-slate-200 rounded-md hover:bg-slate-50"
            >
              ⏱ 模拟超时
            </button>
          </>
        )}

        {milestone.status === 'COMPLETED' && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> 已验收，已放款
          </span>
        )}
      </div>

      {revisionOpen && (
        <RevisionModal bundle={bundle} milestone={milestone} onClose={() => setRevisionOpen(false)} />
      )}
      {submitOpen && (
        <SubmitDeliveryModal bundle={bundle} milestone={milestone} onClose={() => setSubmitOpen(false)} />
      )}
    </div>
  );
};

const RevisionHistory: React.FC<{ milestone: Milestone }> = ({ milestone }) => {
  const history = milestone.revisionHistory || [];
  const [expanded, setExpanded] = useState(false);
  if (history.length === 0) return null;

  // Newest first
  const ordered = [...history].sort((a, b) => b.requestedAt - a.requestedAt);
  const latest = ordered[0];
  const visible = expanded ? ordered : [latest];

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-medium text-orange-800 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> 客户的修改要求
          <span className="ml-1 text-orange-600/70 font-normal">· {history.length} 条</span>
        </div>
        {ordered.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-orange-700 hover:text-orange-900"
          >
            {expanded ? `折叠（仅显示最新）` : `查看全部 ${ordered.length} 条`}
          </button>
        )}
      </div>
      <ol className="space-y-2">
        {visible.map((r, idx) => {
          // index in original chronological order so user sees "第 N 次"
          const seqNo = ordered.length - ordered.indexOf(r);
          return (
            <li key={r.id} className="rounded-md bg-orange-50 border border-orange-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-medium text-orange-900 inline-flex items-center gap-1.5">
                  第 {seqNo} 次修改要求
                  <RevisionCategoryBadge category={r.category} />
                  <span className="text-orange-600/80 font-normal">{formatDateTime(r.requestedAt)}</span>
                </span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  r.resolvedAt
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {r.resolvedAt ? `已返修 · ${formatDate(r.resolvedAt)}` : '待返修'}
                </span>
              </div>
              <div className="text-xs text-orange-900 whitespace-pre-wrap leading-relaxed">{r.reason}</div>
              <div className="text-[11px] text-orange-700 mt-1.5">
                {r.category === 'invalid_delivery'
                  ? <>原截止日：<span className="font-medium">{formatDate(r.returnDueAt)}</span>（不延长）</>
                  : <>返修截止：{formatDate(r.returnDueAt)}</>
                }
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const RevisionCategoryBadge: React.FC<{ category?: 'detail' | 'invalid_delivery' | 'change' }> = ({ category }) => {
  if (!category || category === 'change') return null;
  const map = {
    detail: { text: '细节优化', cls: 'bg-blue-100 text-blue-800' },
    invalid_delivery: { text: '无效交付', cls: 'bg-rose-100 text-rose-800' },
  };
  const m = map[category];
  if (!m) return null;
  return <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded ${m.cls}`}>{m.text}</span>;
};

const MilestoneStatusPill: React.FC<{ status: MilestoneStatus }> = ({ status }) => {
  const map: Record<MilestoneStatus, { text: string; cls: string }> = {
    NOT_STARTED:        { text: '未开始',     cls: 'bg-slate-100 text-slate-600' },
    IN_PROGRESS:        { text: '进行中',     cls: 'bg-blue-100 text-blue-700' },
    SUBMITTED:          { text: '待校验',     cls: 'bg-amber-100 text-amber-700' },
    IN_ACCEPTANCE:      { text: '验收中',     cls: 'bg-amber-100 text-amber-700' },
    REVISION_REQUESTED: { text: '修改中',     cls: 'bg-orange-100 text-orange-700' },
    COMPLETED:          { text: '已完成',     cls: 'bg-emerald-100 text-emerald-700' },
    CANCELLED:          { text: '已取消',     cls: 'bg-slate-100 text-slate-500' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const CoverageBadge: React.FC<{ covered: boolean }> = ({ covered }) => (
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
    covered ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
  }`}>
    {covered ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {covered ? '已资金覆盖' : '未资金覆盖'}
  </span>
);

const StateBadge: React.FC<{ state: string }> = ({ state }) => {
  const map: Record<string, { text: string; cls: string }> = {
    draft:               { text: '草稿',           cls: 'bg-slate-100 text-slate-600' },
    pending_other_side:  { text: '等待对方确认',   cls: 'bg-amber-100 text-amber-700' },
    rejected:            { text: '已拒绝',         cls: 'bg-rose-100 text-rose-700' },
    active:              { text: '进行中',         cls: 'bg-emerald-100 text-emerald-700' },
    completed:           { text: '已完成',         cls: 'bg-blue-100 text-blue-700' },
    cancelled:           { text: '已取消',         cls: 'bg-slate-100 text-slate-500' },
    disputed:            { text: '争议中',         cls: 'bg-rose-100 text-rose-700' },
  };
  const { text, cls } = map[state] || map.draft;
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const PartyChip: React.FC<{ role: string; nickname: string; highlighted?: boolean }> = ({ role, nickname, highlighted }) => (
  <div className={`inline-flex items-center gap-1.5 ${highlighted ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
    <span className="text-xs text-slate-400">{role}：</span>
    <span className={`px-2 py-0.5 rounded ${highlighted ? 'bg-blue-50 text-blue-700' : 'bg-slate-100'}`}>
      {nickname}
    </span>
    {highlighted && <span className="text-[10px] text-blue-600">（你）</span>}
  </div>
);

const Metric: React.FC<{ label: string; value: string; positive?: boolean; hint?: string }> = ({ label, value, positive, hint }) => (
  <div>
    <div className={`text-base font-semibold ${positive ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">
      {label}{hint && <span className="ml-1 text-slate-500">· {hint}</span>}
    </div>
  </div>
);

// =====================================================================
// Plan panels
// =====================================================================

const PaymentPlanPanel: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  const { isViewerClient, dispatch } = useContractsV2();
  const isClient = isViewerClient(bundle);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-700">付款计划（客户 → 平台）</h3>
        {bundle.contract.requiresInvoice && (
          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">需发票（合同级）</span>
        )}
      </div>
      <ul className="space-y-3 text-xs">
        {bundle.paymentPlan.map(p => (
          <li key={p.id} className="border-l-2 pl-3 py-1 border-slate-200">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-slate-900">第 {p.sequenceIndex + 1} 笔 · {formatRmb(p.amount)}</span>
              <PaymentStatusPill status={p.status} />
            </div>
            <div className="text-slate-500 leading-relaxed">
              {paymentTriggerLabel(p, bundle)}
              {p.isFinalTail && <span className="ml-1 text-amber-600">· 尾款</span>}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-1">
              {p.platformConfirmedAt && <span>平台确认 {formatDate(p.platformConfirmedAt)}</span>}
            </div>
            {(p.coversMilestoneIds.length > 0 || p.coversTail) && (
              <div className="text-[10px] text-slate-400 mt-1">
                覆盖：
                {p.coversMilestoneIds
                  .map(id => bundle.milestones.find(m => m.id === id)?.title)
                  .filter(Boolean)
                  .join(' + ')}
                {p.coversTail && (
                  <span className={p.coversMilestoneIds.length > 0 ? 'ml-1 text-amber-700' : 'text-amber-700'}>
                    {p.coversMilestoneIds.length > 0 ? '+ ' : ''}尾款
                  </span>
                )}
              </div>
            )}
            {isClient && (p.status === 'PENDING' || p.status === 'DUE') && (
              <button
                onClick={() => dispatch({ type: 'CLIENT_MARK_PAID', contractId: bundle.contract.id, paymentEntryId: p.id })}
                className="mt-2 text-[11px] px-2 py-1 bg-slate-900 text-white rounded hover:bg-slate-800"
              >
                我已付款
              </button>
            )}
            {p.status === 'PAID_BY_CLIENT' && (
              <button
                onClick={() => dispatch({ type: 'PLATFORM_CONFIRM_PAYMENT', contractId: bundle.contract.id, paymentEntryId: p.id })}
                className="mt-2 text-[10px] px-2 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50"
                title="演示用：模拟平台收款webhook"
              >
                ⏱ 模拟平台收款
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReleasePlanPanel: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">放款计划（平台 → 创作者）</h3>
      <ul className="space-y-2 text-xs">
        {bundle.releasePlan.map(r => {
          const m = bundle.milestones.find(mm => mm.id === r.milestoneId);
          return (
            <li key={r.id} className="border-l-2 pl-3 py-1 border-slate-200">
              <div className="flex items-center justify-between mb-0.5">
                <span className="font-medium text-slate-900">{m?.title || `第 ${r.sequenceIndex + 1} 次`}</span>
                <ReleaseStatusPill status={r.status} />
              </div>
              <div className="text-slate-500">{formatRmb(r.amount)}</div>
              {r.releasedAt && <div className="text-[10px] text-slate-400 mt-0.5">放款于 {formatDate(r.releasedAt)}</div>}
              {r.lateFeeApplied != null && r.lateFeeApplied > 0 && (
                <div className="text-[10px] text-rose-600 mt-0.5">
                  扣除逾期赔付 {formatRmb(r.lateFeeApplied)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const PaymentStatusPill: React.FC<{ status: PaymentEntryStatus }> = ({ status }) => {
  const map: Record<PaymentEntryStatus, { text: string; cls: string }> = {
    PENDING:             { text: '待触发', cls: 'bg-slate-100 text-slate-600' },
    DUE:                 { text: '待付款', cls: 'bg-amber-100 text-amber-700' },
    PAID_BY_CLIENT:      { text: '客户已付', cls: 'bg-blue-100 text-blue-700' },
    PLATFORM_CONFIRMED:  { text: '平台已收', cls: 'bg-emerald-100 text-emerald-700' },
    OVERDUE:             { text: '逾期', cls: 'bg-rose-100 text-rose-700' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const ReleaseStatusPill: React.FC<{ status: ReleasePlanEntry['status'] }> = ({ status }) => {
  const map: Record<ReleasePlanEntry['status'], { text: string; cls: string }> = {
    PENDING:               { text: '未放款',     cls: 'bg-slate-100 text-slate-600' },
    BLOCKED_NO_FUNDING:    { text: '资金不足',   cls: 'bg-rose-100 text-rose-700' },
    RELEASED:              { text: '已放款',     cls: 'bg-emerald-100 text-emerald-700' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

function paymentTriggerLabel(p: PaymentPlanEntry, bundle: ContractV2Bundle): string {
  switch (p.trigger) {
    case 'CONTRACT_SIGNED': return '合同签署后';
    case 'MILESTONE_PASSED': {
      const m = bundle.milestones.find(mm => mm.id === p.triggerMilestoneId);
      return `${m?.title || '指定里程碑'} 验收后`;
    }
    case 'FINAL_TAIL': return '最终验收后';
  }
}

// =====================================================================
// Modals
// =====================================================================

type RevisionReason = 'detail' | 'change' | 'invalid_delivery';

const RevisionModal: React.FC<{
  bundle: ContractV2Bundle; milestone: Milestone;
  onClose: () => void;
}> = ({ bundle, milestone, onClose }) => {
  const { dispatch } = useContractsV2();
  const limit = milestone.revisionLimit;
  const used = milestone.revisionCountUsed;
  const limitReached = used >= limit;

  // Default to 细节优化 unless the limit is reached, in which case
  // the buyer must pick 变更单 or 无效交付 (neither counts).
  const [reason, setReason] = useState<RevisionReason>(limitReached ? 'change' : 'detail');
  const [reasonText, setReasonText] = useState('');
  const [returnDate, setReturnDate] = useState(new Date(Date.now() + 3 * 24 * 3600 * 1000).toISOString().slice(0, 10));

  // Change-specific fields (used when reason === 'change')
  const [changeScope, setChangeScope] = useState('');
  const [changeFeeDelta, setChangeFeeDelta] = useState(0);
  const [changeDays, setChangeDays] = useState(0);

  const detailDisabled = limitReached;

  return (
    <Modal title="客户请求修改 / 变更" onClose={onClose}>
      <p className="text-sm text-slate-500 mb-4">
        请选择本次请求的理由。理由会决定截止日是否延长、是否计入修改次数、以及是否需要额外付款。
      </p>
      <div className="grid grid-cols-3 gap-2.5 mb-5">
        <ChoiceCard
          title="① 细节优化"
          desc={`原需求范围内调整。已用 ${used}/${limit} 次。延长交付时间，免费。`}
          accentColor="blue"
          selected={reason === 'detail'}
          disabled={detailDisabled}
          onClick={() => setReason('detail')}
        />
        <ChoiceCard
          title="② 需求变动 / 新增需求"
          desc="超出原需求。需重新议价并生成变更单：延长交付时间 + 额外付款。"
          accentColor="amber"
          selected={reason === 'change'}
          onClick={() => setReason('change')}
        />
        <ChoiceCard
          title="③ 无效交付"
          desc="本次交付不符合验收标准。不延长截止日；逾期由乙方承担。不计修改次数。"
          accentColor="rose"
          selected={reason === 'invalid_delivery'}
          onClick={() => setReason('invalid_delivery')}
        />
      </div>

      {reason === 'detail' && !limitReached && (
        <>
          <label className="block text-xs text-slate-500 mb-1">具体优化点</label>
          <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} rows={3}
            placeholder="请描述需要细节优化的地方..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <label className="block text-xs text-slate-500 mb-1">双方确认的返修截止时间（延长当前阶段截止日）</label>
          <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!reasonText.trim()}
              onClick={() => {
                dispatch({
                  type: 'CLIENT_REQUEST_REVISION',
                  contractId: bundle.contract.id,
                  milestoneId: milestone.id,
                  reason: reasonText,
                  revisionDueAt: new Date(returnDate).getTime(),
                  category: 'detail',
                });
                onClose();
              }}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-slate-800"
            >
              提交细节优化请求
            </button>
          </div>
        </>
      )}
      {reason === 'detail' && limitReached && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
          已达本里程碑细节优化次数上限（{used}/{limit}），请改为发起"需求变更"或"无效交付"。
        </div>
      )}

      {reason === 'change' && (
        <>
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
            需求变动 / 新增需求会生成变更单：延长交付时间 + 客户额外付款。变更单需创作者同意后生效；费用会折算入下一笔付款，时间会顺延当前阶段截止日。
          </div>
          <label className="block text-xs text-slate-500 mb-1">变更内容 <span className="text-rose-500">*</span></label>
          <textarea value={changeScope} onChange={e => setChangeScope(e.target.value)} rows={3}
            placeholder="例：增加 1 个 30 秒预告片版本"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-amber-100" />
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className="block text-xs text-slate-500 mb-1">追加费用 (¥)</label>
              <input type="number" value={changeFeeDelta} onChange={e => setChangeFeeDelta(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-100" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">追加天数</label>
              <input type="number" value={changeDays} onChange={e => setChangeDays(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-100" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!changeScope.trim()}
              onClick={() => {
                dispatch({
                  type: 'CLIENT_PROPOSE_CHANGE',
                  contractId: bundle.contract.id,
                  milestoneId: milestone.id,
                  scopeDelta: changeScope,
                  feeDelta: changeFeeDelta,
                  additionalDays: changeDays,
                });
                onClose();
              }}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-amber-700"
            >
              提交变更单
            </button>
          </div>
        </>
      )}

      {reason === 'invalid_delivery' && (
        <>
          <div className="text-sm text-rose-800 bg-rose-50 border border-rose-100 rounded-lg p-3 mb-4 leading-relaxed">
            <strong>不延长当前阶段截止日。</strong>
            该里程碑原截止日：<span className="font-medium">{formatDate(milestone.dueAt)}</span>。
            如创作者无法在此之前完成有效交付，将按原合同记为逾期，由乙方承担相应责任。本次请求不计入细节优化次数。
          </div>
          <label className="block text-xs text-slate-500 mb-1">不符合验收标准的具体说明 <span className="text-rose-500">*</span></label>
          <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} rows={4}
            placeholder="请对照验收标准，说明本次交付为何不符合（例如：缺少源文件、画质不达要求、未提供商用授权书等）..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-rose-100" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!reasonText.trim()}
              onClick={() => {
                dispatch({
                  type: 'CLIENT_REQUEST_REVISION',
                  contractId: bundle.contract.id,
                  milestoneId: milestone.id,
                  reason: reasonText,
                  // No extension: returnDueAt stays equal to the
                  // milestone's original 截止日.
                  revisionDueAt: milestone.dueAt,
                  category: 'invalid_delivery',
                });
                onClose();
              }}
              className="px-4 py-2 bg-rose-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-rose-700"
            >
              提交无效交付反馈
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const ChoiceCard: React.FC<{
  title: string; desc: string;
  accentColor: 'blue' | 'amber' | 'rose';
  selected: boolean; disabled?: boolean;
  onClick?: () => void;
}> = ({ title, desc, accentColor, selected, disabled, onClick }) => {
  const palette = {
    blue:  { bg: 'bg-blue-50',  border: 'border-blue-300',  text: 'text-blue-900',  ring: 'ring-blue-200' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', ring: 'ring-amber-200' },
    rose:  { bg: 'bg-rose-50',  border: 'border-rose-300',  text: 'text-rose-900',  ring: 'ring-rose-200' },
  }[accentColor];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left p-3 rounded-lg border transition-colors flex flex-col items-start justify-start ${
        disabled
          ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
          : selected
            ? `${palette.border} ${palette.bg} ring-2 ring-offset-1 ${palette.ring}`
            : 'border-slate-200 hover:bg-slate-50'
      }`}
    >
      <div className={`text-sm font-medium ${selected && !disabled ? palette.text : 'text-slate-900'}`}>{title}</div>
      <div className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</div>
    </button>
  );
};

const SubmitDeliveryModal: React.FC<{ bundle: ContractV2Bundle; milestone: Milestone; onClose: () => void }> = ({ bundle, milestone, onClose }) => {
  const { dispatch } = useContractsV2();

  return (
    <Modal title="提交交付" onClose={onClose}>
      <p className="text-xs text-slate-500 mb-3">
        平台不对"是否为有效交付"做自动判定，仅展示双方约定的验收标准。客户在验收期内确认验收、要求修改或发起争议。
      </p>
      {milestone.acceptanceCriteria && (
        <div className="rounded-md bg-blue-50 border border-blue-100 p-3 mb-4">
          <div className="text-[11px] font-medium text-blue-900 mb-1">验收标准（双方约定）</div>
          <div className="text-xs text-blue-900 whitespace-pre-wrap leading-relaxed">{milestone.acceptanceCriteria}</div>
        </div>
      )}
      <div className="text-[11px] text-slate-500 mb-1">本里程碑约定的交付物</div>
      <ul className="text-xs text-slate-700 space-y-1 mb-4">
        {milestone.requiredAssets.length > 0
          ? milestone.requiredAssets.map((a, i) => <li key={i}>📎 {a.label}</li>)
          : <li className="text-slate-400">（未单独约定具体交付文件）</li>}
      </ul>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button
          onClick={() => {
            dispatch({
              type: 'CREATOR_SUBMIT_DELIVERY',
              contractId: bundle.contract.id,
              milestoneId: milestone.id,
              assets: milestone.requiredAssets.map(a => ({ ...a, uploadedAt: Date.now(), fileUrl: '#' })),
              // Platform doesn't auto-judge — buyer decides during 验收期.
              // Pass true so the milestone enters IN_ACCEPTANCE.
              validityPassed: true,
            });
            onClose();
          }}
          className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
        >
          提交
        </button>
      </div>
    </Modal>
  );
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
      </div>
      {children}
    </div>
  </div>
);

// =====================================================================
// Changes & Disputes
// =====================================================================

const ChangesSection: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  const { isViewerCreator, dispatch } = useContractsV2();
  if (bundle.changes.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
      <h2 className="text-sm font-medium text-slate-700 mb-3">变更单</h2>
      <ul className="space-y-3">
        {bundle.changes.map(c => (
          <li key={c.id} className="border-l-2 border-amber-200 pl-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-900">
                由 {c.proposedBy === 'client' ? '客户' : '创作者'} 发起
              </span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                c.status === 'PENDING_AGREE' ? 'bg-amber-100 text-amber-700'
                  : c.status === 'AGREED' ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-rose-100 text-rose-700'
              }`}>
                {c.status === 'PENDING_AGREE' ? '待对方同意' : c.status === 'AGREED' ? '已同意' : '已拒绝'}
              </span>
            </div>
            <div className="text-sm text-slate-700">{c.scopeDelta}</div>
            <div className="text-xs text-slate-500 mt-1">追加 {formatRmb(c.feeDelta)} · 顺延 {c.additionalDays} 天</div>
            {isViewerCreator(bundle) && c.status === 'PENDING_AGREE' && (
              <div className="mt-2 flex gap-2">
                <button onClick={() => dispatch({ type: 'AGREE_CHANGE', contractId: bundle.contract.id, changeId: c.id })}
                  className="px-3 py-1 bg-emerald-600 text-white rounded text-xs">同意</button>
                <button onClick={() => dispatch({ type: 'REJECT_CHANGE', contractId: bundle.contract.id, changeId: c.id })}
                  className="px-3 py-1 border border-slate-200 text-slate-600 rounded text-xs">拒绝</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DisputesSection: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  if (bundle.disputes.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-rose-200 bg-rose-50/50 p-5 mt-4">
      <h2 className="text-sm font-medium text-rose-700 mb-3 inline-flex items-center gap-2">
        <ShieldAlert className="w-4 h-4" /> 争议处理
      </h2>
      <ul className="space-y-2 text-sm">
        {bundle.disputes.map(d => (
          <li key={d.id}>
            <span className="font-medium">{d.raisedBy === 'client' ? '客户' : '创作者'}</span> 发起争议 ·
            <span className="text-slate-700 ml-1">{d.reason}</span>
            <div className="text-xs text-slate-500 mt-0.5">{formatDateTime(d.raisedAt)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ContractDetailPage;
